import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Loading } from "../../components/shared";

export default function ResidentMeetingDetail() {
  const { meetingId } = useParams();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api
      .get(`/resident-meetings/meetings/${meetingId}`)
      .then((response) => setData(response.data))
      .catch(() => setData({ missing: true }));
  }, [meetingId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitRsvp = async (status) => {
    try {
      await api.post(`/resident-meetings/meetings/${meetingId}/rsvp`, {
        status,
      });
      toast.success("RSVP recorded");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not record RSVP");
    }
  };

  if (!data) return <Loading />;
  if (data.missing) {
    return (
      <p
        className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600"
        data-testid="resident-meeting-missing"
      >
        This meeting is not available.
      </p>
    );
  }

  const {
    meeting,
    minutes,
    resolutions,
    supporting_documents: documents,
  } = data;
  return (
    <article data-testid="resident-meeting-detail-page">
      <Link
        className="text-sm font-semibold text-blue-700 hover:text-blue-900"
        data-testid="resident-meeting-back"
        to="/resident/meetings"
      >
        ← All meetings
      </Link>
      <header className="mt-5 border-b border-slate-200 pb-6">
        <p className="text-xs font-semibold text-blue-700">
          {meeting.meeting_type.replaceAll("_", " ")} · {meeting.meeting_number}
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-slate-900">
          {meeting.title}
        </h1>
        <p className="mt-3 text-sm text-slate-600">
          {meeting.scheduled_at.replace("T", " ").slice(0, 16)} ·{" "}
          {meeting.location || "Location to be confirmed"}
        </p>
        {meeting.description && (
          <p className="mt-4 whitespace-pre-wrap text-sm leading-7 text-slate-800">
            {meeting.description}
          </p>
        )}
      </header>
      <section className="mt-7" data-testid="resident-meeting-agenda">
        <h2 className="font-display text-xl font-semibold">Agenda</h2>
        <ol className="mt-3 space-y-2">
          {meeting.agenda_items.map((item) => (
            <li
              className="border border-slate-200 bg-white p-4 text-sm"
              key={item.agenda_id}
            >
              <strong>
                {item.sequence}. {item.title}
              </strong>
              {item.notes && (
                <p className="mt-1 text-slate-600">{item.notes}</p>
              )}
            </li>
          ))}
        </ol>
      </section>
      <section
        className="mt-7 border border-blue-200 bg-blue-50 p-5"
        data-testid="resident-meeting-rsvp"
      >
        <h2 className="font-display text-lg font-semibold">Your RSVP</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {["YES", "MAYBE", "NO"].map((status) => (
            <Button
              data-testid={`resident-meeting-rsvp-${status.toLowerCase()}`}
              key={status}
              onClick={() => submitRsvp(status)}
              size="sm"
              variant={meeting.rsvp_status === status ? "default" : "outline"}
            >
              {status === "YES"
                ? "Attending"
                : status === "NO"
                  ? "Not attending"
                  : "Maybe"}
            </Button>
          ))}
        </div>
      </section>
      {documents.length > 0 && (
        <section
          className="mt-8 border-t border-slate-200 pt-6"
          data-testid="resident-meeting-documents"
        >
          <h2 className="font-display text-xl font-semibold">
            Supporting documents
          </h2>
          <div className="mt-3 space-y-2">
            {documents.map((document) => (
              <a
                className={
                  "flex items-center justify-between gap-3 border border-slate-200 bg-white " +
                  "p-3 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                }
                data-testid={`resident-meeting-document-${document.doc_id}`}
                href={`${API}/resident-communications/documents/${document.doc_id}/download`}
                key={document.doc_id}
                rel="noreferrer"
                target="_blank"
              >
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {document.title}
                </span>
                <Download className="h-4 w-4" />
              </a>
            ))}
          </div>
        </section>
      )}
      {meeting.has_published_minutes && (
        <section
          className="mt-8 border-t border-slate-200 pt-6"
          data-testid="resident-published-minutes"
        >
          <h2 className="font-display text-xl font-semibold">
            Published proceedings & minutes
          </h2>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-slate-800">
            {minutes.body}
          </p>
          {minutes.action_items?.length > 0 && (
            <ul
              className="mt-4 list-disc space-y-1 pl-5 text-sm text-slate-700"
              data-testid="resident-meeting-actions"
            >
              {minutes.action_items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          {resolutions.length > 0 && (
            <div className="mt-6" data-testid="resident-meeting-resolutions">
              <h3 className="font-display text-lg font-semibold">
                Resolutions
              </h3>
              <div className="mt-3 space-y-2">
                {resolutions.map((item) => (
                  <article
                    className="border border-slate-200 bg-white p-4 text-sm"
                    key={item.resolution_id}
                  >
                    <strong>
                      {item.sequence}. {item.title}
                    </strong>
                    <p className="mt-2 whitespace-pre-wrap text-slate-700">
                      {item.text}
                    </p>
                    {item.outcome && (
                      <p className="mt-2 text-xs font-semibold text-blue-700">
                        {item.outcome}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-slate-500">
                      Administrative record: {item.recorded_for || 0} for ·{" "}
                      {item.recorded_against || 0} against ·{" "}
                      {item.recorded_abstain || 0} abstain
                    </p>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </article>
  );
}
